import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';
import { dashboardAPI, medibuddyAPI } from './api';

const COLORS = {
  aquaBlue: '#11a8a4',
  grassGreen: '#7ed957',
  darkGrey: '#3c3b3f',
  vividPurple: '#9440dd',
  white: '#ffffff',
  lightGrey: '#f3f4f6',
  mediumGrey: '#e5e7eb',
  darkText: '#1f2937',
  mediumText: '#6b7280',
  lightText: '#9ca3af',
  orange: '#f97316'
};

const DownloadReports = () => {
  const { campId } = useParams();
  const navigate = useNavigate();
  const [groupedReports, setGroupedReports] = useState({});
  const [filteredReports, setFilteredReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campInfo, setCampInfo] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [reportTypes, setReportTypes] = useState(['All']);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [brandingKey, setBrandingKey] = useState(null);
  const [downloadingAllBranded, setDownloadingAllBranded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDateFilter, setActiveDateFilter] = useState('All');
  const [reportDates, setReportDates] = useState(['All']);
  const [isMedibuddyTechnician, setIsMedibuddyTechnician] = useState(false);
  const [technicianData, setTechnicianData] = useState(null);
  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const location = useLocation();

  useEffect(() => {
    fetchReports();
  }, [campId]);

  useEffect(() => {
    applyFilter(activeFilter);
  }, [groupedReports, activeFilter,activeDateFilter]);

  // Check if assigned technician belongs to "Medibuddy Technician" group
  useEffect(() => {
    const checkMedibuddyTechnician = async () => {
      if (!campId) return;
      try {
        // Step 1: Get technician assignments for this camp
        const assignments = await medibuddyAPI.getTechnicianAssignments(campId);
        if (!assignments || assignments.length === 0) return;

        const assignment = assignments[0];
        const techEmail = assignment.technician?.user?.email;
        if (!techEmail) return;

        // Step 2: Fetch clients to check group membership
        const clients = await medibuddyAPI.getClients();
        if (!clients) return;

        const clientArray = Array.isArray(clients) ? clients : (clients.data || []);
        const matchedClient = clientArray.find(
          (c) => c.email === techEmail || c.id === assignment.technician?.user?.id
        );

        if (matchedClient && matchedClient.groups) {
          const isMedibuddy = matchedClient.groups.some(
            (g) => g.name === 'Medibuddy Technician'
          );
          setIsMedibuddyTechnician(isMedibuddy);
          setTechnicianData(assignment);
        }
      } catch (err) {
        console.error('Error checking Medibuddy technician status:', err);
      }
    };
    checkMedibuddyTechnician();
  }, [campId]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // Use the centralized API
      const reportsData = await dashboardAPI.getDownloadReports(campId);
      // Optionally fetch camp info if needed
      // const campInfoData = await dashboardAPI.getCampInfo(campId);

      // Group reports by patient name and include patient_excel_id
      const reports = reportsData || [];
      const grouped = reports.reduce((acc, report) => {
        const patientKey = `${report.patient_name}_${report.patient_excel_id || 'NO_ID'}`;
        if (!acc[patientKey]) {
          acc[patientKey] = {
            patient_name: report.patient_name,
            patient_excel_id: report.patient_excel_id || 'NO_ID',
            reports: []
          };
        }
        acc[patientKey].reports.push({
          type: report.report_type,
          pdf_link: report.pdf_link,
          // Store patient_excel_id at report level too for easier access
          patient_excel_id: report.patient_excel_id,
          nirman_number: report.nirman_number || null,
          report_date: report.report_date 
        });
        return acc;
      }, {});

      // Extract unique report types for filter buttons
      const uniqueTypes = [...new Set(reports.map(report => report.report_type))].sort();
      setReportTypes(['All', ...uniqueTypes]);

      //ADD THIS - EXTRACT UNIQUE DATES for date filter (done by shyam date 27-01-2026)
      const uniqueDates = [...new Set(reports.map(report => report.report_date))].sort((a, b) => {
        // Sort dates in descending order (newest first)
        const dateA = a.split('-').reverse().join('-');
        const dateB = b.split('-').reverse().join('-');
        return dateB.localeCompare(dateA);
      });
      setReportDates(['All', ...uniqueDates]);

      setGroupedReports(grouped);
      // setCampInfo(campInfoData || null); // Uncomment if you fetch camp info
      setError('');
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError('Failed to fetch reports. Please try again.');
      setGroupedReports({});
    } finally {
      setLoading(false);
    }
  };
  const handleExportWhatsappLogs = async () => {
  try {
    const response = await axios.get(
      `https://cbackend.xraidigital.com/api/technician/export-whatsapp-logs/${campId}/`,
      { responseType: "blob" } // 👈 this is required
    );

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    // Use backend filename if available, else default
    const contentDisposition = response.headers["content-disposition"];
    let fileName = `whatsapp_logs_camp_${campId}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match.length === 2) fileName = match[1];
    }

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error exporting WhatsApp logs:", error);
    alert("An error occurred while exporting WhatsApp logs.");
  }
};

const handleDateFilterChange = (dateFilter) => {
  setActiveDateFilter(dateFilter);
};


  const applyFilter = (filterType) => {
     let filtered = {};
     
     Object.keys(groupedReports).forEach(patientKey => {
       const patient = groupedReports[patientKey];
       const filteredPatientReports = patient.reports.filter(report => {
         // Check report type filter
         const typeMatch = activeFilter === 'All' || report.type === activeFilter;
         // Check date filter
         const dateMatch = activeDateFilter === 'All' || report.report_date === activeDateFilter;
         
         return typeMatch && dateMatch;
       });
       
       if (filteredPatientReports.length > 0) {
         filtered[patientKey] = {
           ...patient,
           reports: filteredPatientReports
         };
       }
     });
     
     setFilteredReports(filtered);
   };

  const handleFilterChange = (filterType) => {
    setActiveFilter(filterType);
  };

  const normalizeText = (text) =>
  text
    .trim()                  // remove leading/trailing spaces
    .replace(/\s+/g, ' ')    // collapse multiple spaces
    .replace(/[^a-zA-Z0-9 ]/g, '') // remove special chars
    .replace(/\s/g, '_');    // spaces → underscore

  // Generate unique filename with timestamp to avoid duplicates
  const generateUniqueFilename = (patientExcelId, patientName, reportType, nirmanNumber = null) => {
    const cleanPatientName = patientName ? normalizeText(patientName) : 'UNKNOWN_NAME';

    if (reportType === "NorthStar_Assesment_Form" && nirmanNumber) {
        const cleanNirmanNumber = normalizeText(nirmanNumber);
        return `${cleanNirmanNumber}_${cleanPatientName}.pdf`;
    }

    const cleanPatientId = (patientExcelId && patientExcelId !== 'NO_ID') ? normalizeText(patientExcelId) : 'UNKNOWN_ID';
    const cleanReportType = reportType ? normalizeText(reportType) : 'UNKNOWN_TYPE';

    return `${cleanPatientId}_${cleanPatientName}_${cleanReportType}.pdf`;
};

  const handleDownload = async (reportUrl, patientName, patientExcelId, reportType, reportIndex = null, nirmanNumber = null) => {
    if (reportUrl) {
      try {
        const response = await fetch(reportUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        // Generate unique filename using patient_excel_id, patient_name, and report_type
        link.download = generateUniqueFilename(patientExcelId, patientName, reportType, nirmanNumber);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error(`Error downloading ${patientName} ${reportType} report:`, error);
        alert('Error downloading the report');
      }
    } else {
      alert('Report not available for download');
    }
  };

  const brandingImageCache = {};

  const getBrandingImageBytes = async (fileName) => {
    if (brandingImageCache[fileName]) {
      return brandingImageCache[fileName];
    }
    const url = `${process.env.PUBLIC_URL}/${fileName}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load header/footer image ${fileName} (status ${response.status})`);
    }
    const bytes = await response.arrayBuffer();
    brandingImageCache[fileName] = bytes;
    return bytes;
  };

  const collectFilteredReports = () => {
    const allReports = [];
    Object.values(filteredReports).forEach(patient => {
      patient.reports.forEach((report, index) => {
        if (report.pdf_link) {
          const patientExcelId = patient.patient_excel_id || report.patient_excel_id || "UNKNOWN_ID";
          const filename = generateUniqueFilename(
            patientExcelId,
            patient.patient_name,
            report.type,
            report.nirman_number,
            index
          );
          allReports.push({
            url: report.pdf_link,
            filename: filename,
            patientName: patient.patient_name,
            patientExcelId: patientExcelId,
            reportType: report.type,
          });
        }
      });
    });
    return allReports;
  };

  const createBrandedReportBlob = async (reportUrl) => {
    const response = await fetch(reportUrl);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();

    const [headerBytes, footerBytes] = await Promise.all([
      getBrandingImageBytes('bsad.png'),
      getBrandingImageBytes('bottom.png')
    ]);

    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    const headerImage = await pdfDoc.embedPng(headerBytes);
    const footerImage = await pdfDoc.embedPng(footerBytes);

    for (const page of pages) {
      const { width, height } = page.getSize();

      let headerHeight = width * (200 / 2480);
      const maxHeaderHeight = height * 0.15;
      if (headerHeight > maxHeaderHeight) headerHeight = maxHeaderHeight;
      const headerWidth = headerHeight * (2480 / 200);
      page.drawImage(headerImage, {
        x: 0,
        y: height + 3 - headerHeight,
        width: headerWidth,
        height: headerHeight
      });

      let footerTopFromTop = 780;
      let footerWidth = width + 30;
      let footerHeight = footerWidth * (170 / 2000);
      const availableBelowFooter = height - 780;
      if (availableBelowFooter >= 20) {
        const maxFooterHeight = availableBelowFooter - 10;
        if (footerHeight > maxFooterHeight) {
          footerHeight = maxFooterHeight;
          footerWidth = footerHeight * (2000 / 170);
        }
        footerTopFromTop = 780 + (availableBelowFooter - footerHeight) / 2;
      } else {
        footerHeight = Math.min(footerHeight, height * 0.12);
        footerTopFromTop = height - footerHeight - 5;
        footerWidth = footerHeight * (2000 / 170);
      }
      const footerX = (width - footerWidth) / 2;
      page.drawImage(footerImage, {
        x: footerX,
        y: height - footerTopFromTop - footerHeight,
        width: footerWidth,
        height: footerHeight
      });
    }

    const brandedBytes = await pdfDoc.save();
    return new Blob([brandedBytes], { type: 'application/pdf' });
  };

  const handleDownloadBranded = async (reportUrl, patientName, patientExcelId, reportType, reportIndex, nirmanNumber = null) => {
    const key = `${patientName}_${patientExcelId}_${reportType}_${reportIndex}`;
    if (!reportUrl) {
      alert('Report not available for download');
      return;
    }
    setBrandingKey(key);
    try {
      const brandedBlob = await createBrandedReportBlob(reportUrl);
      const downloadUrl = window.URL.createObjectURL(brandedBlob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = generateUniqueFilename(patientExcelId, patientName, reportType, nirmanNumber);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(`Error creating branded report for ${patientName} ${reportType}:`, error);
      alert('Error creating the branded report. Please try again.');
    } finally {
      setBrandingKey(null);
    }
  };

  const handleDownloadAllBranded = async () => {
    const allReports = collectFilteredReports();

    if (allReports.length === 0) {
      alert("No reports available for download");
      return;
    }

    if (allReports.length > 100) {
      const confirmDownload = window.confirm(
        `You are about to download ${allReports.length} branded reports. This may take several minutes and consume significant memory. Do you want to continue?`
      );
      if (!confirmDownload) return;
    }

    setDownloadingAllBranded(true);
    setDownloadProgress({ current: 0, total: allReports.length });

    try {
      const zip = new JSZip();
      const failedDownloads = [];
      const BATCH_SIZE = 5;

      for (let i = 0; i < allReports.length; i += BATCH_SIZE) {
        const batch = allReports.slice(i, Math.min(i + BATCH_SIZE, allReports.length));

        const batchPromises = batch.map(async (report, batchIndex) => {
          const actualIndex = i + batchIndex;
          setDownloadProgress({ current: actualIndex + 1, total: allReports.length });

          try {
            const fileData = await createBrandedReportBlob(report.url);
            return { fileData, report };
          } catch (error) {
            console.error(`Error downloading branded ${report.filename}:`, error);
            return { fileData: null, report };
          }
        });

        const batchResults = await Promise.all(batchPromises);

        batchResults.forEach(({ fileData, report }) => {
          if (fileData) {
            zip.file(report.filename, fileData);
          } else {
            failedDownloads.push(report.filename);
          }
        });

        if (i + BATCH_SIZE < allReports.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      let zipFilename = `Camp_${campId}`;

      if (activeFilter !== "All") {
        zipFilename += `_${activeFilter}`;
      }

      if (activeDateFilter !== "All") {
        zipFilename += `_${activeDateFilter.replace(/\//g, '-')}`;
      }

      if (activeFilter === "All" && activeDateFilter === "All") {
        zipFilename += `_All_Branded_Reports`;
      } else {
        zipFilename += `_Branded_Reports`;
      }

      zipFilename += `.zip`;

      const content = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });

      saveAs(content, zipFilename);

      if (failedDownloads.length > 0) {
        alert(
          `Download completed! ${allReports.length - failedDownloads.length} files downloaded successfully. ${failedDownloads.length} files failed to download.`
        );
      } else {
        alert(`All ${allReports.length} branded reports downloaded successfully!`);
      }
    } catch (error) {
      console.error("Error creating branded zip file:", error);
      alert("Error occurred while creating the branded zip file. Please try again.");
    } finally {
      setDownloadingAllBranded(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  const downloadFileAsBlob = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      return { blob, filename };
    } catch (error) {
      console.error(`Error downloading ${filename}:`, error);
      return null;
    }
  };

  const handleDownloadAll = async (
  reportUrl,
  patientName,
  patientExcelId,
  reportType,
  reportIndex = null, // you can keep it if you want
  nirmanNumber = null) => {
    const allReports = collectFilteredReports();

    if (allReports.length === 0) {
      alert("No reports available for download");
      return;
    }

    // Warn if downloading a large number of files
    if (allReports.length > 100) {
      const confirmDownload = window.confirm(
        `You are about to download ${allReports.length} reports. This may take several minutes and consume significant memory. Do you want to continue?`
      );
      if (!confirmDownload) return;
    }

    setDownloadingAll(true);
    setDownloadProgress({ current: 0, total: allReports.length });

    try {
      const zip = new JSZip();
      const failedDownloads = [];
      const BATCH_SIZE = 5; // Process 5 files at a time to reduce memory usage

      // Process files in batches
      for (let i = 0; i < allReports.length; i += BATCH_SIZE) {
        const batch = allReports.slice(i, Math.min(i + BATCH_SIZE, allReports.length));
        
        // Download batch concurrently
        const batchPromises = batch.map(async (report, batchIndex) => {
          const actualIndex = i + batchIndex;
          setDownloadProgress({ current: actualIndex + 1, total: allReports.length });
          
          try {
            const fileData = await downloadFileAsBlob(report.url, report.filename);
            return { fileData, report };
          } catch (error) {
            console.error(`Error downloading ${report.filename}:`, error);
            return { fileData: null, report };
          }
        });

        const batchResults = await Promise.all(batchPromises);

        // Add successfully downloaded files to zip
        batchResults.forEach(({ fileData, report }) => {
          if (fileData) {
            zip.file(report.filename, fileData.blob);
          } else {
            failedDownloads.push(report.filename);
          }
        });

        // Small delay between batches to allow garbage collection
        if (i + BATCH_SIZE < allReports.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

   //       All reports, All dates: Camp_1_All_Reports.zip
   // Audiometry, All dates: Camp_1_Audiometry_Reports.zip
   // All reports, 27-01-2026: Camp_1_27-01-2026_Reports.zip
   // Audiometry, 27-01-2026: Camp_1_Audiometry_27-01-2026_Reports.zip
    let zipFilename = `Camp_${campId}`;
    
    // Add filter type to filename
    if (activeFilter !== "All") {
      zipFilename += `_${activeFilter}`;
    }
    
    // Add date filter to filename
    if (activeDateFilter !== "All") {
      zipFilename += `_${activeDateFilter.replace(/\//g, '-')}`;
    }
    
    // Add default suffix if no filters applied
    if (activeFilter === "All" && activeDateFilter === "All") {
      zipFilename += `_All_Reports`;
    } else {
      zipFilename += `_Reports`;
    }
    
    zipFilename += `.zip`;

      const content = await zip.generateAsync({ 
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 } // Moderate compression
      });
      
      saveAs(content, zipFilename);

      // Show summary
      if (failedDownloads.length > 0) {
        alert(
          `Download completed! ${allReports.length - failedDownloads.length} files downloaded successfully. ${failedDownloads.length} files failed to download.`
        );
      } else {
        alert(`All ${allReports.length} reports downloaded successfully!`);
      }
    } catch (error) {
      console.error("Error creating zip file:", error);
      alert("Error occurred while creating the zip file. Please try again. If the problem persists, try downloading fewer reports at a time.");
    } finally {
      setDownloadingAll(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };


  const handleBack = () => {
    if (location.state?.from === 'upload-report') {
      navigate('/dashboard', {
        state: { activeMenuItem: 'upload-report' }
      });
    } else {
      navigate(-1);
    }
  };

  const getReportTypeColor = (reportType) => {
    switch (reportType.toLowerCase()) {
      case 'audiometry':
        return COLORS.aquaBlue;
      case 'optometry':
        return COLORS.grassGreen;
      case 'doctor':
        return COLORS.vividPurple;
      case 'vitals':
        return COLORS.orange;
      default:
        return COLORS.darkGrey;
    }
  };

  const getFilterButtonColor = (filterType) => {
    switch (filterType.toLowerCase()) {
      case 'all':
        return COLORS.darkGrey;
      case 'audiometry':
        return COLORS.aquaBlue;
      case 'optometry':
        return COLORS.grassGreen;
      case 'doctor':
        return COLORS.vividPurple;
      case 'vitals':
        return COLORS.orange;
      default:
        return COLORS.mediumGrey;
    }
  };

  const getTotalReportsCount = () => {
    return Object.values(groupedReports).reduce((total, patient) => total + patient.reports.length, 0);
  };

  const getFilteredReportsCount = () => {
    return Object.values(filteredReports).reduce((total, patient) => total + patient.reports.length, 0);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: COLORS.lightGrey 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `4px solid ${COLORS.mediumGrey}`,
            borderTop: `4px solid ${COLORS.aquaBlue}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: 'auto'
          }}></div>
          <p style={{ marginTop: '16px', color: COLORS.mediumText }}>Loading reports...</p>
        </div>
      </div>
    );
  }

  const patientsList = Object.values(filteredReports).filter((patient) =>
    patient.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    patient.patient_excel_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: COLORS.lightGrey,
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          border: `1px solid ${COLORS.mediumGrey}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: COLORS.darkText, 
              margin: 0 
            }}>
              Download Patient Reports
            </h1>
            <p style={{ 
              color: COLORS.mediumText, 
              marginTop: '4px',
              margin: '4px 0 0 0'
            }}>
              Camp ID: {campId} {campInfo && `- ${campInfo.location}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {isMedibuddyTechnician && (
              <button
                onClick={() => navigate(`/medibuddy-package/${campId}`)}
                style={{
                  backgroundColor: COLORS.vividPurple,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Medibuddy Package
              </button>
            )}
            <button
              onClick={handleBack}
              style={{
                backgroundColor: COLORS.aquaBlue,
                color: COLORS.white,
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#b91c1c'
          }}>
            {error}
          </div>
        )}

        {/* Search bar - now searches both name and ID */}
        <div style={{ marginBottom: '16px', maxWidth: '280px' }}>
          <input
            type="text"
            placeholder="Search patient name or ID..."
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={downloadingAll || downloadingAllBranded}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.mediumGrey}`,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
        

        {/* Filter Buttons */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '8px',
          border: `1px solid ${COLORS.mediumGrey}`,
          padding: '20px 24px',
          marginBottom: '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div>
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: COLORS.darkText,
                margin: '0 0 8px 0'
              }}>
                Filter Reports by Type
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: COLORS.mediumText,
                margin: 0
              }}>
                Total: {getTotalReportsCount()} reports | Showing: {getFilteredReportsCount()} reports
              </p>
            </div>
            
            {/* Download All Buttons */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleDownloadAll}
                disabled={downloadingAll || downloadingAllBranded || getFilteredReportsCount() === 0}
                style={{
                  backgroundColor: (downloadingAll || downloadingAllBranded) ? COLORS.mediumGrey : COLORS.orange,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: (downloadingAll || downloadingAllBranded) || getFilteredReportsCount() === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  minWidth: '140px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!downloadingAll && !downloadingAllBranded && getFilteredReportsCount() > 0) {
                    e.target.style.backgroundColor = COLORS.vividPurple;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!downloadingAll && !downloadingAllBranded && getFilteredReportsCount() > 0) {
                    e.target.style.backgroundColor = COLORS.orange;
                  }
                }}
              >
                {downloadingAll ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: `2px solid ${COLORS.white}30`,
                      borderTop: `2px solid ${COLORS.white}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Download All ({getFilteredReportsCount()})
                  </>
                )}
              </button>
              <button
                onClick={handleDownloadAllBranded}
                disabled={downloadingAll || downloadingAllBranded || getFilteredReportsCount() === 0}
                style={{
                  backgroundColor: (downloadingAll || downloadingAllBranded) ? COLORS.mediumGrey : COLORS.grassGreen,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: (downloadingAll || downloadingAllBranded) || getFilteredReportsCount() === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  minWidth: '140px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!downloadingAll && !downloadingAllBranded && getFilteredReportsCount() > 0) {
                    e.target.style.backgroundColor = '#5cba41';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!downloadingAll && !downloadingAllBranded && getFilteredReportsCount() > 0) {
                    e.target.style.backgroundColor = COLORS.grassGreen;
                  }
                }}
              >
                {downloadingAllBranded ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: `2px solid ${COLORS.white}30`,
                      borderTop: `2px solid ${COLORS.white}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Download All with Logo ({getFilteredReportsCount()})
                  </>
                )}
              </button>
            </div>
            <button
  onClick={handleExportWhatsappLogs}
  style={{
    backgroundColor: COLORS.vividPurple,
    color: COLORS.white,
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }}
>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
  Export WhatsApp Logs
</button>

          </div>

          

          {/* Download Progress */}
          {(downloadingAll || downloadingAllBranded) && (
            <div style={{
              backgroundColor: COLORS.lightGrey,
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px',
              border: `1px solid ${COLORS.mediumGrey}`
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', color: COLORS.darkText, fontWeight: '500' }}>
                  {downloadingAllBranded ? 'Branding and Downloading Reports...' : 'Downloading Reports...'}
                </span>
                <span style={{ fontSize: '14px', color: COLORS.mediumText }}>
                  {downloadProgress.current} / {downloadProgress.total}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: COLORS.mediumGrey,
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(downloadProgress.current / downloadProgress.total) * 100}%`,
                  height: '100%',
                  backgroundColor: downloadingAllBranded ? COLORS.grassGreen : COLORS.orange,
                  transition: 'width 0.3s ease',
                  borderRadius: '4px'
                }}></div>
              </div>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            
            {reportTypes.map((reportType) => (
              <button
                key={reportType}
                onClick={() => handleFilterChange(reportType)}
                disabled={downloadingAll}
                style={{
                  backgroundColor: activeFilter === reportType ? getFilterButtonColor(reportType) : COLORS.white,
                  color: activeFilter === reportType ? COLORS.white : getFilterButtonColor(reportType),
                  border: `2px solid ${getFilterButtonColor(reportType)}`,
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: downloadingAll ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: downloadingAll ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (activeFilter !== reportType && !downloadingAll) {
                    e.target.style.backgroundColor = `${getFilterButtonColor(reportType)}10`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeFilter !== reportType && !downloadingAll) {
                    e.target.style.backgroundColor = COLORS.white;
                  }
                }}
              >
                {reportType === 'All' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                )}
                {reportType === 'Audiometry' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                )}
                {reportType === 'Optometry' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
                {reportType === 'Doctor' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
                {reportType === 'Vitals' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                )}
                {reportType}
                {activeFilter === reportType && (
                  <span style={{
                    backgroundColor: `${COLORS.white}30`,
                    borderRadius: '10px',
                    padding: '2px 6px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    {getFilteredReportsCount()}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        

         
        {/* Date Filter Buttons   done by shyam 27-01-2026*/}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '8px',
          border: `1px solid ${COLORS.mediumGrey}`,
          padding: '20px 24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: COLORS.darkText,
            margin: '0 0 12px 0'
          }}>
            Filter by Date
          </h3>
          
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {reportDates.map((date) => (
              <button
                key={date}
                onClick={() => handleDateFilterChange(date)}
                disabled={downloadingAll}
                style={{
                  backgroundColor: activeDateFilter === date ? COLORS.grassGreen : COLORS.white,
                  color: activeDateFilter === date ? COLORS.white : COLORS.grassGreen,
                  border: `2px solid ${COLORS.grassGreen}`,
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: downloadingAll ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: downloadingAll ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (activeDateFilter !== date && !downloadingAll) {
                    e.target.style.backgroundColor = `${COLORS.grassGreen}10`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeDateFilter !== date && !downloadingAll) {
                    e.target.style.backgroundColor = COLORS.white;
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {date}
              </button>
            ))}
          </div>
        </div>

        {/* Reports List */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '8px',
          border: `1px solid ${COLORS.mediumGrey}`,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${COLORS.mediumGrey}`,
            backgroundColor: COLORS.lightGrey
          }}>
            <h2 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: COLORS.darkText,
              margin: 0
            }}>
              {activeFilter === 'All' ? 'All Patient Reports' : `${activeFilter} Reports`} ({patientsList.length} patients)
            </h2>
          </div>

          {patientsList.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: COLORS.mediumText 
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ margin: 'auto', marginBottom: '16px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                {activeFilter === 'All' ? 'No reports available' : `No ${activeFilter} reports available`}
              </p>
              <p style={{ fontSize: '14px', color: COLORS.lightText }}>
                {activeFilter === 'All' 
                  ? 'Reports will appear here once they are generated for patients.'
                  : `Try selecting a different report type or check "All" to see available reports.`
                }
              </p>
            </div>
          ) : (
            <div style={{ padding: '0' }}>
              {patientsList.map((patient, index) => (
                <div
                  key={`${patient.patient_name}-${patient.patient_excel_id}-${index}`}
                  style={{
                    padding: '20px 24px',
                    borderBottom: index < patientsList.length - 1 ? `1px solid ${COLORS.mediumGrey}` : 'none',
                    transition: 'background-color 0.2s',
                    opacity: downloadingAll ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => !downloadingAll && (e.currentTarget.style.backgroundColor = COLORS.lightGrey)}
                  onMouseLeave={(e) => !downloadingAll && (e.currentTarget.style.backgroundColor = COLORS.white)}
                >
                  <div>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <h3 style={{ 
                          margin: 0, 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: COLORS.darkText
                        }}>
                          {patient.patient_name}
                        </h3>
                        <span style={{
                          backgroundColor: COLORS.aquaBlue,
                          color: COLORS.white,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          ID: {patient.patient_excel_id || 'NO_ID'}
                        </span>
                      </div>
                      <span style={{
                        backgroundColor: COLORS.mediumGrey,
                        color: COLORS.darkGrey,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {activeFilter === 'All' 
                          ? `${patient.reports.length} report${patient.reports.length !== 1 ? 's' : ''} available`
                          : `${patient.reports.length} ${activeFilter} report${patient.reports.length !== 1 ? 's' : ''}`
                        }
                      </span>
                    </div>
                    
                    {/* Reports for this patient */}
                    <div style={{ 
                      display: 'grid', 
                      gap: '12px',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))'
                    }}>
                      {patient.reports.map((report, reportIndex) => (
                          <div
                            key={`${patient.patient_name}-${patient.patient_excel_id}-${report.type}-${reportIndex}`}
                            style={{
                              backgroundColor: COLORS.lightGrey,
                              border: `1px solid ${COLORS.mediumGrey}`,
                              borderRadius: '6px',
                              padding: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                backgroundColor: `${getReportTypeColor(report.type)}20`,
                                color: getReportTypeColor(report.type),
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}>
                                {report.type}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleDownload(
                                  report.pdf_link,
                                  patient.patient_name,
                                  patient.patient_excel_id,
                                  report.type,
                                  reportIndex,
                                  report.nirman_number
                                )}
                                disabled={downloadingAll || downloadingAllBranded}
                                style={{
                                  backgroundColor: (downloadingAll || downloadingAllBranded) ? COLORS.mediumGrey : COLORS.aquaBlue,
                                  color: COLORS.white,
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  cursor: (downloadingAll || downloadingAllBranded) ? 'not-allowed' : 'pointer',
                                  fontWeight: '500',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  if (!downloadingAll && !downloadingAllBranded) {
                                    e.target.style.backgroundColor = COLORS.vividPurple;
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!downloadingAll && !downloadingAllBranded) {
                                    e.target.style.backgroundColor = COLORS.aquaBlue;
                                  }
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Report
                              </button>
                              <button
                                onClick={() => handleDownloadBranded(
                                  report.pdf_link,
                                  patient.patient_name,
                                  patient.patient_excel_id,
                                  report.type,
                                  reportIndex,
                                  report.nirman_number
                                )}
                                disabled={(downloadingAll || downloadingAllBranded) || brandingKey === `${patient.patient_name}_${patient.patient_excel_id}_${report.type}_${reportIndex}`}
                                style={{
                                  backgroundColor: ((downloadingAll || downloadingAllBranded) || brandingKey === `${patient.patient_name}_${patient.patient_excel_id}_${report.type}_${reportIndex}`)
                                    ? COLORS.mediumGrey
                                    : COLORS.grassGreen,
                                  color: COLORS.white,
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  cursor: ((downloadingAll || downloadingAllBranded) || brandingKey === `${patient.patient_name}_${patient.patient_excel_id}_${report.type}_${reportIndex}`) ? 'not-allowed' : 'pointer',
                                  fontWeight: '500',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s',
                                  minWidth: '110px',
                                  justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => {
                                  if (!downloadingAll && !downloadingAllBranded && brandingKey !== `${patient.patient_name}_${patient.patient_excel_id}_${report.type}_${reportIndex}`) {
                                    e.target.style.backgroundColor = '#5cba41';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!downloadingAll && !downloadingAllBranded && brandingKey !== `${patient.patient_name}_${patient.patient_excel_id}_${report.type}_${reportIndex}`) {
                                    e.target.style.backgroundColor = COLORS.grassGreen;
                                  }
                                }}
                              >
                                {brandingKey === `${patient.patient_name}_${patient.patient_excel_id}_${report.type}_${reportIndex}` ? (
                                  <>
                                    <div style={{
                                      width: '12px',
                                      height: '12px',
                                      border: `2px solid ${COLORS.white}30`,
                                      borderTop: `2px solid ${COLORS.white}`,
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite'
                                    }}></div>
                                    Branding...
                                  </>
                                ) : (
                                  <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Download Branded Report
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DownloadReports;